import { Injectable } from '@angular/core';
import { firstValueFrom, timer } from 'rxjs';
import { ApiService } from './api.service';
import { VariantService } from './variant.service';

interface VariantUpdateUserInfoResponse {
  session_uuid: string;
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  updated: boolean;
}

const MAX_ATTEMPTS = 4;
const RETRY_DELAY_MS = 500;
const SYNCED_FINGERPRINT_KEY = 'variant.userInfoSyncedFingerprint';

@Injectable({ providedIn: 'root' })
export class VariantUserInfoService {
  private inFlightFingerprint: string | null = null;

  constructor(private api: ApiService, private variantService: VariantService) {}

  /**
   * Pushes first/last/email to the variant `users` row for this session, retrying
   * up to MAX_ATTEMPTS times. Skips the call entirely if this exact data was
   * already synced successfully; a change to any of the three fields (or a prior
   * failure) makes it eligible to run again on the next call.
   */
  syncUserInfo(firstName: string, lastName: string, email: string): void {
    const sessionUuid = this.variantService.getSessionUuid();
    if (!sessionUuid) return;

    const fp = this.fingerprint(sessionUuid, firstName, lastName, email);
    if (this.inFlightFingerprint === fp) return;
    if (this.readSyncedFingerprint() === fp) return;

    this.inFlightFingerprint = fp;
    void this.attempt(sessionUuid, firstName, lastName, email, fp, 1);
  }

  private async attempt(
    sessionUuid: string,
    firstName: string,
    lastName: string,
    email: string,
    fp: string,
    attemptNumber: number
  ): Promise<void> {
    const isFinalAttempt = attemptNumber >= MAX_ATTEMPTS;
    const payload = {
      session_uuid: sessionUuid,
      first_name: (firstName || '').trim() || null,
      last_name: (lastName || '').trim() || null,
      email: (email || '').trim() || null,
      is_final_attempt: isFinalAttempt
    };

    try {
      await firstValueFrom(
        this.api.dispatch<VariantUpdateUserInfoResponse>('/variants/update_user_info', payload)
      );
      this.writeSyncedFingerprint(fp);
      if (this.inFlightFingerprint === fp) this.inFlightFingerprint = null;
    } catch {
      if (isFinalAttempt) {
        // Retry budget exhausted; backend already sent one failure email for this
        // attempt (is_final_attempt=true). Clear in-flight so the next "See
        // Available Slots" click starts a fresh 4-attempt cycle.
        if (this.inFlightFingerprint === fp) this.inFlightFingerprint = null;
        return;
      }
      try {
        await firstValueFrom(timer(RETRY_DELAY_MS * attemptNumber));
      } catch {}
      await this.attempt(sessionUuid, firstName, lastName, email, fp, attemptNumber + 1);
    }
  }

  private fingerprint(sessionUuid: string, first: string, last: string, email: string): string {
    return [
      sessionUuid,
      (first || '').trim().toLowerCase(),
      (last || '').trim().toLowerCase(),
      (email || '').trim().toLowerCase()
    ].join('|');
  }

  private readSyncedFingerprint(): string | null {
    try {
      return sessionStorage.getItem(SYNCED_FINGERPRINT_KEY);
    } catch {
      return null;
    }
  }

  private writeSyncedFingerprint(fp: string): void {
    try {
      sessionStorage.setItem(SYNCED_FINGERPRINT_KEY, fp);
    } catch {}
  }
}
